'use client'

/**
 * El copy de este bloque NO vive aquí: quien monta el bloque lo resuelve en
 * servidor (`resolveCallPreview`, en `lib/guides/call-preview-copy`) y lo pasa
 * como prop. Si las tablas de los siete idiomas vivieran dentro de este módulo,
 * cada visitante —y cada landing y cada guía— las descargaría enteras en el JS
 * de cliente. ADR-036.
 *
 * La prop es el modelo resuelto ENTERO (`preview`), no character + copy por
 * separado: así el componente no puede recibir un personaje con la copy de
 * otro idioma o de otro personaje. La frontera de esa resolución es de build
 * (`import 'server-only'` en el módulo de copy) y la revisa
 * `pnpm lint:conventions` sobre el grafo de imports (revisión de #450).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { Check, Play, Sparkles, Video } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { GuideLocale } from '@/lib/guides'
import { CHARACTER_THEMES, type CharacterTheme } from '@/lib/character-themes'
import type { GuideCharacterKey } from '@/lib/guides'
import type { ResolvedCallPreview } from '@/lib/guides/call-preview-copy'
import { getCharacterPreviewVideo } from '@/lib/characters'
import { captureProductEvent, PRODUCT_EVENTS } from '@/lib/product-analytics'

// El destino del CTA vive en lib/guides/character-cta (lo comparte con la
// oferta posterior a imprimir); aquí solo queda lo propio del preview.
const PREVIEW_CONFIG: Record<GuideCharacterKey, { poster: string; previewCharacterId: string }> = {
  santa: {
    poster: '/videos/santa-main-poster.webp',
    previewCharacterId: 'santa',
  },
  reyes: {
    poster: '/reyes/melchor.webp',
    previewCharacterId: 'melchor-video',
  },
  'tooth-fairy': {
    poster: '/hada/hada.webp',
    previewCharacterId: 'tooth-fairy-video',
  },
  // Sin clip de preview: el bloque cae al retrato y oculta el botón de play.
  easter: {
    poster: '/generic/generic-conejo-pascua/generic-conejo-pascua-card.webp',
    previewCharacterId: 'generic-conejo-pascua',
  },
  'raton-perez': {
    poster: '/raton-perez/raton-perez.webp',
    previewCharacterId: 'raton-perez-video',
  },
  // Sin clip de preview, como `easter`: el bloque cae al retrato de Bruma el
  // fantasmita, que es quien protagoniza el hero de /halloween.
  halloween: {
    poster: '/generic/generic-fantasma/generic-fantasma-card.webp',
    previewCharacterId: 'generic-fantasma',
  },
}

export type GuideCallPreviewAnalytics = {
  surface: 'guide'
  events: {
    viewed: typeof PRODUCT_EVENTS.GUIDE_CALL_PREVIEW_VIEWED
    played: typeof PRODUCT_EVENTS.GUIDE_CALL_PREVIEW_PLAYED
    ctaClicked: typeof PRODUCT_EVENTS.GUIDE_CALL_PREVIEW_CTA_CLICKED
  }
  properties: {
    character_id: GuideCharacterKey
    locale: GuideLocale
    guide_slug: string
    placement: 'after_first_section'
  }
}

export type CitationCallPreviewAnalytics = {
  surface: 'citation_hub'
  events: {
    viewed: typeof PRODUCT_EVENTS.CALL_PREVIEW_VIEWED
    played: typeof PRODUCT_EVENTS.CALL_PREVIEW_PLAYED
    ctaClicked: typeof PRODUCT_EVENTS.CALL_PREVIEW_CTA_CLICKED
  }
  properties: {
    character_id: 'raton-perez'
    locale: 'es'
    page: 'estadisticas/dientes-de-leche'
  }
}

/**
 * Analytics is a discriminated union: a surface owns its event names and
 * property shape, so a guide payload cannot be paired with citation events.
 */
export type CallPreviewAnalytics = GuideCallPreviewAnalytics | CitationCallPreviewAnalytics

export type CallPreviewProps = {
  /**
   * Personaje, locale y copy ya casados en servidor. Un objeto suelto, porque
   * props separadas dejaban construir el estado imposible de "papá Noel con la
   * copy del ratón" (revisión de #450).
   */
  preview: ResolvedCallPreview
  ctaLabel?: string
  ctaHref: string
  analytics: CallPreviewAnalytics
  testId?: string
}

export function CallPreview({
  preview,
  ctaLabel,
  ctaHref,
  analytics,
  testId = 'call-preview',
}: CallPreviewProps) {
  // El personaje y el copy llegan casados de servidor; aquí solo se separan
  // para leerlos, no para recombinarlos.
  const { character, sharedCopy, characterCopy } = preview
  const [showVideo, setShowVideo] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)
  const impressionCaptured = useRef(false)
  const config = PREVIEW_CONFIG[character]
  const theme = CHARACTER_THEMES[character]
  const videoSrc = getCharacterPreviewVideo(config.previewCharacterId)
  const analyticsProperties = useMemo(() => analytics.properties, [analytics.properties])

  useEffect(() => {
    const section = sectionRef.current
    if (!section || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || impressionCaptured.current) return
        impressionCaptured.current = true
        captureProductEvent(analytics.events.viewed, analyticsProperties)
        observer.disconnect()
      },
      { threshold: 0.5 },
    )

    observer.observe(section)
    return () => observer.disconnect()
  }, [analytics.events.viewed, analyticsProperties])

  function handlePlay() {
    setShowVideo(true)
    captureProductEvent(analytics.events.played, analyticsProperties)
  }

  function handleCtaClick() {
    captureProductEvent(analytics.events.ctaClicked, analyticsProperties)
  }

  return (
    <section
      ref={sectionRef}
      data-testid={testId}
      className="call-preview overflow-hidden rounded-3xl border border-white/20 bg-gradient-to-br from-white/15 via-white/10 to-black/20 shadow-2xl"
    >
      <div className="grid md:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] md:items-stretch">
        <CallPreviewMedia
          theme={theme}
          poster={config.poster}
          videoSrc={videoSrc}
          videoLabel={characterCopy.videoLabel}
          playLabel={sharedCopy.play}
          callBadge={sharedCopy.callBadge}
          showVideo={showVideo}
          onPlay={handlePlay}
        />

        <div className="flex flex-col justify-center p-6 md:p-8">
          <p className={`flex items-center gap-2 text-sm font-semibold uppercase tracking-wide ${theme.videoCall.accentText}`}>
            <Sparkles className="size-4" aria-hidden />
            {characterCopy.eyebrow}
          </p>
          <h2 className="font-christmas mt-3 text-2xl font-bold leading-tight text-white md:text-3xl">
            {characterCopy.title}
          </h2>
          <p className="mt-3 leading-relaxed text-white/80">{characterCopy.description}</p>
          <ul className="mt-5 space-y-2 text-sm text-white/90">
            {characterCopy.benefits.map((benefit) => (
              <li key={benefit} className="flex items-center gap-2">
                <Check className={`size-4 shrink-0 ${theme.videoCall.accentText}`} aria-hidden />
                {benefit}
              </li>
            ))}
          </ul>
          <div className="mt-6">
            <Button
              size="lg"
              asChild
              className={`font-christmas w-full rounded-full text-base font-bold shadow-lg md:w-auto ${theme.videoCall.ctaButton}`}
            >
              <Link
                href={ctaHref}
                onClick={handleCtaClick}
              >
                <Video className="size-5" aria-hidden />
                {ctaLabel ?? sharedCopy.cta}
              </Link>
            </Button>
            <p className="mt-2 text-center text-xs text-white/60 md:text-left">
              {sharedCopy.reassurance}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}/**
 * Columna de medios del bloque: retrato con botón de play y, al pulsarlo, el
 * clip de preview. Sin clip (personajes del catálogo, que aún no lo tienen) se
 * queda en el retrato y NO muestra el botón — abriría un reproductor vacío.
 */
function CallPreviewMedia({
  theme,
  poster,
  videoSrc,
  videoLabel,
  playLabel,
  callBadge,
  showVideo,
  onPlay,
}: {
  theme: CharacterTheme
  poster: string
  videoSrc: string | undefined
  videoLabel: string
  playLabel: string
  callBadge: string
  showVideo: boolean
  onPlay: () => void
}) {
  return (
  // `min-w-0` no es decorativo: `aspect-video` + `min-h-56` implican por la
  // relación de aspecto un ancho mínimo de 224 × 16/9 = 398 px, y como hijo de
  // grid el `min-width: auto` por defecto deja que ese mínimo gane a la pista.
  // En un móvil de 390 px la tarjeta se salía a 423 px y el `overflow-hidden`
  // de la sección le recortaba 58 px de imagen por la derecha. Con `min-w-0`
  // manda la pista y es el alto quien cede.
  <div className={`relative aspect-video min-h-56 w-full min-w-0 overflow-hidden md:aspect-auto md:min-h-full ${theme.heroBg}`}>
    {showVideo && videoSrc ? (
      <video
        autoPlay
        controls
        loop
        muted
        playsInline
        preload="metadata"
        poster={poster}
        aria-label={videoLabel}
        className="absolute inset-0 h-full w-full object-cover"
      >
        {videoSrc && <source src={videoSrc} type="video/mp4" />}
      </video>
    ) : (
      <>
        <Image
          src={poster}
          alt=""
          fill
          sizes="(min-width: 768px) 380px, calc(100vw - 48px)"
          className="object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-black/10" />
        {videoSrc && (
        <button
          type="button"
          onClick={onPlay}
          aria-label={playLabel}
          className="group absolute inset-0 flex cursor-pointer items-center justify-center focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-white"
        >
          <span className="flex items-center gap-3 rounded-full border border-white/40 bg-black/65 px-5 py-3 font-semibold text-white shadow-xl backdrop-blur-sm transition group-hover:scale-105 group-hover:bg-black/75 motion-reduce:transform-none">
            <Play className="size-5 fill-current" aria-hidden />
            {playLabel}
          </span>
        </button>
        )}
        <span className="absolute left-3 top-3 flex items-center gap-2 rounded-full bg-black/65 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm">
          <span className={`size-2 animate-pulse rounded-full motion-reduce:animate-none ${theme.videoCall.accentSolid}`} />
          {callBadge}
        </span>
      </>
    )}
  </div>
  )
}
